<template>
  <div class="venue-info-block" :class="{ 'venue-info-block--modal': isModal }">
    <div class="venue-info-block__content">
      <template v-if="data">
        <opening-hours-item
          v-if="data.servingHours?.length"
          icon="Cutlry"
          :class="{ ...detectComponentVariant(variantedComponents.OPENING_HOURS) }"
          heading-tag="h4"
          :title="t('serving-hours')"
          :opening-hours="{ value: data.servingHours }"
        />
        <template v-if="hygieneRatingBadge || data.awards?.length">
          <h4 class="venue-info-block__ratings-heading font-m">
            {{ t('reviews-modal-ratings-and-awards') }}
          </h4>
          <div v-if="hygieneRatingBadge" class="venue-info-block__award">
            <div class="venue-info-block__award-image">
              <img
                v-if="hygieneRatingBadge.src"
                :src="hygieneRatingBadge.src"
                :alt="hygieneRatingBadge.alt || ''"
              />
            </div>
            <div class="venue-info-block__award-text">
              <h5>{{ t('reviews-modal-food-hygiene-rating') }}</h5>
              <p v-if="hygieneRatingDate" class="venue-info-block__award-last-updated">
                Last updated on {{ hygieneRatingDate }}
              </p>
            </div>
          </div>
          <template v-if="data.awards?.length">
            <div v-for="award in data.awards" :key="award.name" class="venue-info-block__award">
              <div class="venue-info-block__award-image">
                <img :src="award.image.src" :alt="award.image.alt || ''" />
              </div>
              <div class="venue-info-block__award-text">
                <h5 class="venue-info-block__award-title">{{ award.name }}</h5>
              </div>
            </div>
          </template>
        </template>
      </template>
      <template v-else>
        <div class="venue-info-block__loader">
          <div aria-hidden="true" class="venue-info-block__dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <span>{{ t('rating-loading') }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import OpeningHoursItem from '../OpeningHoursItem/OpeningHoursItem.vue';
import { useDetectVariantByBrand, variantedComponents } from '../../../utils/componentVariants';
import { getFoodHygieneRatingBadge } from '../../../assets/food-hygiene-ratings/_fhr';
import { useVenueInfoQuery } from '../../../utils/queries/useVenueInfoQuery';

const { t } = useI18n();

const detectComponentVariant = useDetectVariantByBrand();

const props = defineProps({
  venueId: {
    type: String,
    required: true,
  },
  isModal: {
    type: Boolean,
    default: () => true,
  },
});

const { data } = useVenueInfoQuery(props.venueId);

const hygieneRatingBadge = ref(null);

const hygieneRatingDate = computed(() => {
  if (data.value?.hygieneVisitDate) {
    return dayjs(data.value.hygieneVisitDate).format('ddd D MMM YYYY');
  }
  return null;
});

watch(
  data,
  async () => {
    const val = data?.value;
    if (val?.hygieneRating && val?.venueCountry && !hygieneRatingBadge.value) {
      const badge = await getFoodHygieneRatingBadge(val.hygieneRating, val.venueCountry);
      hygieneRatingBadge.value = badge;
    }
  },
  { immediate: true }
);
</script>

<style lang="scss">
@import './scss/VenueInfoBlock.scss';
</style>
